import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, ClipboardList, PhoneCall, Star, FlaskConical, CheckCheck, Ban, MessageSquare, LogOut, Users, KeyRound, ChevronDown, FolderOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const LOGO_URL = "/clinic-logo.webp";

const BASE_NAV = [
  { to: "/",           label: "Dashboard",  icon: LayoutDashboard, end: true,  testid: "nav-dashboard" },
  { to: "/daily-log",  label: "Daily Log",  icon: ClipboardList,   end: false, testid: "nav-daily-log" },
  { to: "/follow-ups", label: "Follow-ups", icon: PhoneCall,       end: false, testid: "nav-follow-ups" },
  { to: "/lab-work",   label: "Lab Work",   icon: FlaskConical,    end: false, testid: "nav-lab-work" },
  { to: "/incomplete", label: "Incomplete follow-up/treatment", icon: Ban, end: false, testid: "nav-incomplete" },
  { to: "/completed",  label: "Completed Treatment",  icon: CheckCheck,      end: false, testid: "nav-completed" },
  { to: "/reviews",    label: "Reviews",    icon: Star,            end: false, testid: "nav-reviews" },
  { to: "/message-templates", label: "Message Templates", icon: MessageSquare, end: false, testid: "nav-message-templates" },
  { to: "/patient-resources", label: "Patient Resources", icon: FolderOpen, end: false, testid: "nav-patient-resources" },
];

export default function Layout({ children }) {
  const { user, logout, changePassword, setError } = useAuth();
  const nav = useNavigate();
  const [pwOpen, setPwOpen] = useState(false);
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [pwError, setPwError] = useState("");

  const doLogout = async () => { await logout(); nav("/login", { replace: true }); };

  const items = user?.role === "owner"
    ? [...BASE_NAV, { to: "/users", label: "Users", icon: Users, end: false, testid: "nav-users" }]
    : BASE_NAV;

  const submitPw = async (e) => {
    e.preventDefault();
    setPwBusy(true); setPwError("");
    try {
      const { api, formatApiError } = await import("@/lib/api");
      await api.patch("/auth/me/password", { current_password: curPw, new_password: newPw });
      toast.success("Password updated");
      setPwOpen(false); setCurPw(""); setNewPw("");
    } catch (err) {
      const { formatApiError } = await import("@/lib/api");
      setPwError(formatApiError(err) || "Unable to change password.");
    } finally {
      setPwBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      <aside className="hidden md:flex md:w-64 lg:w-72 flex-col border-r border-slate-200 bg-white">
        <div className="h-20 flex items-center px-6 border-b border-slate-200">
          <img src={LOGO_URL} alt="Lucid Dental" className="h-9 w-auto"
               data-testid="sidebar-logo" />
        </div>
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {items.map(({ to, label, icon: Icon, end, testid }) => (
            <NavLink key={to} to={to} end={end} data-testid={testid}
              className={({ isActive }) => cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150",
                isActive ? "bg-sky-50 text-sky-700" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
              )}
            >
              <Icon size={18} /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-200">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="w-full flex items-center gap-3 px-2 py-2 rounded-md hover:bg-slate-50"
                data-testid="user-menu-trigger"
              >
                <div className="h-9 w-9 rounded-full bg-sky-100 text-sky-700 flex items-center justify-center font-semibold">
                  {user?.name?.[0]?.toUpperCase() || "U"}
                </div>
                <div className="min-w-0 flex-1 text-left">
                  <div className="text-sm font-medium text-slate-900 truncate" data-testid="current-user-name">{user?.name}</div>
                  <div className="text-xs text-slate-500 truncate">{user?.email}</div>
                </div>
                <ChevronDown size={16} className="text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => setPwOpen(true)} data-testid="change-password-menu">
                <KeyRound size={14} className="mr-2" /> Change password
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={doLogout} data-testid="logout-button" className="text-red-600 focus:text-red-700">
                <LogOut size={14} className="mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <div className="md:hidden h-14 border-b border-slate-200 bg-white flex items-center justify-between px-4">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Lucid Dental" className="h-7 w-auto" />
          </Link>
          <Button size="sm" variant="ghost" onClick={doLogout} data-testid="logout-button-mobile">
            <LogOut size={16} />
          </Button>
        </div>
        <main className="flex-1 overflow-x-hidden">{children}</main>
      </div>

      {/* Change password */}
      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle>Change password</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitPw} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="cur-pw">Current password</Label>
              <Input id="cur-pw" type="password" required value={curPw}
                     onChange={(e) => setCurPw(e.target.value)}
                     data-testid="current-password-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-pw">New password</Label>
              <Input id="new-pw" type="password" required minLength={6} value={newPw}
                     onChange={(e) => setNewPw(e.target.value)}
                     placeholder="Min 6 characters" data-testid="new-password-input" />
            </div>
            {pwError && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2"
                   data-testid="change-password-error">
                {pwError}
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPwOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={pwBusy} className="bg-sky-500 hover:bg-sky-600 text-white"
                      data-testid="submit-change-password">
                {pwBusy ? "Saving…" : "Update password"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
